import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquare, Plus, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, ROLE_TR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

function fmtTime(iso) {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
        ? d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
}

export default function Messages() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const [convs, setConvs] = useState(null);
    const [active, setActive] = useState(null);
    const [msgs, setMsgs] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [newOpen, setNewOpen] = useState(false);
    const [suppliers, setSuppliers] = useState([]);
    const [newSup, setNewSup] = useState("");
    const [newSubject, setNewSubject] = useState("");
    const [newMsg, setNewMsg] = useState("");
    const scrollRef = useRef(null);
    const isSupplier = user?.role === "supplier";

    const loadConvs = useCallback(async (keepActive) => {
        const r = await api.get("/messages/conversations");
        setConvs(r.data);
        if (keepActive) {
            setActive((cur) => (cur ? r.data.find((c) => c.conversation_id === cur.conversation_id) || cur : cur));
        }
    }, []);

    const loadMsgs = useCallback(async (convId) => {
        const r = await api.get(`/messages/conversations/${convId}/messages`);
        setMsgs(r.data);
    }, []);

    useEffect(() => {
        loadConvs().catch(() => {});
        api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    }, [loadConvs]);

    useEffect(() => {
        const target = params.get("conv");
        if (target && convs && !active) {
            const c = convs.find((x) => x.conversation_id === target);
            if (c) {
                setActive(c);
                loadMsgs(c.conversation_id);
            }
        }
    }, [params, convs, active, loadMsgs]);

    useEffect(() => {
        if (!active) return;
        const t = setInterval(() => loadMsgs(active.conversation_id).catch(() => {}), 12000);
        return () => clearInterval(t);
    }, [active, loadMsgs]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [msgs]);

    const openConv = async (c) => {
        setActive(c);
        await loadMsgs(c.conversation_id);
        loadConvs(true);
    };

    const send = async () => {
        const content = text.trim();
        if (!content || !active || sending) return;
        setSending(true);
        try {
            await api.post(`/messages/conversations/${active.conversation_id}/messages`, { content });
            setText("");
            await loadMsgs(active.conversation_id);
            loadConvs(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Mesaj gönderilemedi");
        } finally {
            setSending(false);
        }
    };

    const createConv = async () => {
        if (!newSup || !newMsg.trim()) return toast.error("Tedarikçi seçin ve mesaj yazın");
        try {
            const r = await api.post("/messages/conversations", {
                supplier_id: newSup,
                subject: newSubject || undefined,
                content: newMsg.trim(),
            });
            toast.success("Yazışma başlatıldı");
            setNewOpen(false);
            setNewSup(""); setNewSubject(""); setNewMsg("");
            await loadConvs();
            setActive(r.data);
            loadMsgs(r.data.conversation_id);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Oluşturulamadı");
        }
    };

    const totalUnread = (convs || []).reduce((a, c) => a + (c.unread || 0), 0);

    return (
        <div className="space-y-6" data-testid="messages-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Mesajlar</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {isSupplier ? "Mağaza ve işletmelerle yazışmalarınız" : "Tedarikçilerinizle yazışmalarınız"}
                        {totalUnread > 0 && <span className="ml-2 rounded-full bg-terra-500 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="messages-unread-total">{totalUnread} okunmamış</span>}
                    </p>
                </div>
                {!isSupplier && (
                    <Button onClick={() => setNewOpen(true)} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="new-message-button">
                        <Plus className="mr-2 h-4 w-4" /> Yeni Yazışma
                    </Button>
                )}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm lg:col-span-4" data-testid="conversation-list">
                    <div className="border-b bg-muted/50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Yazışmalar</p>
                    </div>
                    <div className="max-h-[560px] overflow-y-auto scrollbar-thin">
                        {!convs && [...Array(3)].map((_, i) => <div key={i} className="m-3 h-16 animate-pulse rounded-xl bg-muted" />)}
                        {convs?.map((c) => (
                            <button
                                key={c.conversation_id}
                                onClick={() => openConv(c)}
                                className={`w-full border-b px-4 py-3.5 text-left transition-colors last:border-0 hover:bg-muted/50 ${active?.conversation_id === c.conversation_id ? "bg-terra-600/5 border-l-2 border-l-terra-600" : ""}`}
                                data-testid={`conversation-${c.conversation_id}`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <p className="truncate text-sm font-bold">{isSupplier ? c.buyer_company_name : c.supplier_name}</p>
                                    <span className="shrink-0 text-[10px] text-muted-foreground">{fmtTime(c.last_at)}</span>
                                </div>
                                <p className="mt-0.5 truncate text-xs font-medium text-terra-600">{c.subject}</p>
                                <div className="mt-0.5 flex items-center justify-between gap-2">
                                    <p className="truncate text-xs text-muted-foreground">{c.last_message || "—"}</p>
                                    {c.unread > 0 && <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-terra-500 px-1 text-[10px] font-bold text-white" data-testid={`unread-badge-${c.conversation_id}`}>{c.unread}</span>}
                                </div>
                            </button>
                        ))}
                        {convs && !convs.length && (
                            <div className="p-10 text-center" data-testid="conversations-empty">
                                <MessageSquare className="mx-auto h-8 w-8 text-muted-foreground" />
                                <p className="mt-3 text-sm text-muted-foreground">Henüz yazışma yok.</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col overflow-hidden rounded-2xl border bg-card shadow-sm lg:col-span-8" data-testid="message-thread-panel">
                    {!active ? (
                        <div className="flex flex-1 flex-col items-center justify-center p-16 text-center" data-testid="thread-empty">
                            <MessageSquare className="h-10 w-10 text-muted-foreground" />
                            <p className="mt-4 text-sm text-muted-foreground">Bir yazışma seçin veya yeni yazışma başlatın.</p>
                        </div>
                    ) : (
                        <>
                            <div className="border-b bg-muted/50 px-5 py-3.5">
                                <p className="font-display font-bold" data-testid="thread-title">{isSupplier ? active.buyer_company_name : active.supplier_name}</p>
                                <p className="text-xs text-muted-foreground">Konu: {active.subject}</p>
                            </div>
                            <div ref={scrollRef} className="min-h-[380px] flex-1 space-y-3 overflow-y-auto bg-muted/30 p-5 scrollbar-thin" data-testid="message-thread">
                                {msgs.map((m) => {
                                    const mine = m.sender_user_id === user?.user_id;
                                    return (
                                        <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.message_id}`}>
                                            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-terra-600 text-white" : "border bg-card"}`}>
                                                {!mine && <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wide opacity-70">{m.sender_name} • {ROLE_TR[m.sender_role] || m.sender_role}</p>}
                                                <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                                                <p className={`mt-1 text-right text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>{fmtTime(m.created_at)}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t p-4">
                                <Input
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder="Mesajınızı yazın..."
                                    disabled={sending}
                                    data-testid="message-input"
                                />
                                <Button type="submit" disabled={sending || !text.trim()} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="message-send-button">
                                    <Send className="h-4 w-4" />
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>

            <Dialog open={newOpen} onOpenChange={setNewOpen}>
                <DialogContent data-testid="new-conversation-dialog">
                    <DialogHeader><DialogTitle className="font-display">Yeni Yazışma</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <select
                            className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                            value={newSup}
                            onChange={(e) => setNewSup(e.target.value)}
                            data-testid="new-conversation-supplier"
                        >
                            <option value="">Tedarikçi seçin...</option>
                            {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.name} ({s.city})</option>)}
                        </select>
                        <Input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder="Konu (örn: Eylül ayı fiyat listesi)" data-testid="new-conversation-subject" />
                        <Textarea value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Mesajınız..." rows={4} data-testid="new-conversation-message" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewOpen(false)}>Vazgeç</Button>
                        <Button onClick={createConv} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="new-conversation-submit"><Send className="mr-2 h-4 w-4" /> Gönder</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
